import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { CheckinSettings, getCheckinSettings, saveCheckinSettings } from '../../../../api/stays-checkin-client';
import { StaysApiError } from '../../../../api/stays-client';
import { AppButton } from '../../../button/app-button';
import { Field, FieldError, FieldHint, Input, Label } from '../../../form-kit';
import { StateSwitch } from '../../../../motion';
import { FormSkeleton, LoadError } from '../../dashboard/dashboard-shared-components/load-state';
import { refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import toast from '../../../../utils/toast';
import { layout, palette, radius, spacing, typography } from '../../../../style-utils/theme';

/**
 * THE LISTING'S CHECK-IN SETTINGS (plan S6, the Check-in tab): send the link
 * automatically when an imported booking brings a guest e-mail; ask for the
 * trip's purpose; the host name the guest reads. Who verifies an ID is NOT
 * here — it is the platform's rule (every adult, the owner's answer).
 */

const Block = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  min-width: 0;
`;

const H = styled.h3`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const Check = styled.label`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${spacing.sm};
  align-items: center;
  min-height: ${layout.touchTarget};
  padding: ${spacing.xs} ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.ink};
  cursor: pointer;

  input {
    width: ${spacing.md};
    height: ${spacing.md};
    margin: 0;
    accent-color: ${palette.teal};
  }
`;

export const CheckinSettingsForm: React.FC<{ listingId: string; canManage: boolean }> = ({ listingId, canManage }) => {
  const [view, setView] = useState<'loading' | 'error' | 'ready'>('loading');
  const [form, setForm] = useState<CheckinSettings | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const read = useCallback(async () => {
    setView('loading');
    try {
      setForm(await getCheckinSettings(listingId));
      setView('ready');
    } catch {
      setView('error');
    }
  }, [listingId]);

  useEffect(() => {
    void read();
  }, [read]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form || busy) return;
    setBusy(true);
    setFields({});
    try {
      setForm(await saveCheckinSettings(listingId, form));
      toast.success('Check-in settings saved');
    } catch (err) {
      const x = err as StaysApiError;
      if (x && x.status === 400 && Object.keys(x.fields || {}).length) setFields(x.fields);
      toast.error(refusalText(x, 'Something went wrong. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Block aria-labelledby="checkin-settings-title">
      <H id="checkin-settings-title">Check-in settings</H>
      <StateSwitch state={view}>
        {view === 'loading' ? <FormSkeleton fields={3} label="check-in settings" /> : null}
        {view === 'error' ? <LoadError message="Couldn't load the check-in settings" onRetry={() => void read()} /> : null}
        {view === 'ready' && form ? (
          <form onSubmit={save} noValidate aria-label="Check-in settings">
            <Check htmlFor={`auto-send-${listingId}`}>
              <input id={`auto-send-${listingId}`} type="checkbox" checked={form.auto_send_link} disabled={!canManage}
                onChange={e => setForm({ ...form, auto_send_link: e.target.checked })} />
              <span>Send the check-in link automatically when a guest email arrives</span>
            </Check>
            <Check htmlFor={`purpose-${listingId}`}>
              <input id={`purpose-${listingId}`} type="checkbox" checked={form.purpose_required} disabled={!canManage}
                onChange={e => setForm({ ...form, purpose_required: e.target.checked })} />
              <span>Travel purpose is required</span>
            </Check>
            <Field>
              <Label htmlFor={`host-name-${listingId}`}>Host name shown to guests</Label>
              <Input
                id={`host-name-${listingId}`}
                value={form.host_display_name}
                maxLength={60}
                readOnly={!canManage}
                hasError={!!fields.host_display_name}
                onChange={e => setForm({ ...form, host_display_name: e.target.value })}
                aria-describedby={`host-name-hint-${listingId}`}
              />
              <FieldHint id={`host-name-hint-${listingId}`}>Left empty, guests see the host&apos;s first name.</FieldHint>
              <FieldError message={fields.host_display_name} />
            </Field>
            {canManage ? (
              <AppButton type="submit" variant="secondary" loading={busy} loadingLabel="Saving">
                Save settings
              </AppButton>
            ) : null}
          </form>
        ) : null}
      </StateSwitch>
    </Block>
  );
};
